import { useState, useEffect, useRef, useCallback } from "react";
import {
  Shield, Plus, Trash2, Copy, Check, Eye, EyeOff, Loader2,
  KeyRound, Share2, Users, X, UserPlus, Camera, ScanLine, ClipboardPaste,
} from "lucide-react";
import { colors, fonts as defaultFonts } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAuthenticatorEntries,
  createAuthenticatorEntry,
  deleteAuthenticatorEntry,
  shareAuthenticatorEntry,
} from "../services/api";

// ── Base32 decode ──────────────────────────────────────────────
const BASE32_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
function base32Decode(input) {
  const str = input.replace(/=+$/, "").toUpperCase();
  let bits = 0;
  let value = 0;
  const output = [];
  for (const ch of str) {
    const idx = BASE32_CHARS.indexOf(ch);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(output);
}

// ── TOTP generation (Web Crypto) ───────────────────────────────
async function generateTOTP(secret, period = 30, digits = 6, algorithm = "SHA1") {
  const key = base32Decode(secret);
  const time = Math.floor(Date.now() / 1000 / period);
  const timeBytes = new ArrayBuffer(8);
  const view = new DataView(timeBytes);
  view.setUint32(4, time, false);

  const algoMap = { SHA1: "SHA-1", SHA256: "SHA-256", SHA512: "SHA-512" };
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: algoMap[algorithm] || "SHA-1" },
    false,
    ["sign"]
  );
  const hmac = await crypto.subtle.sign("HMAC", cryptoKey, timeBytes);
  const hmacBytes = new Uint8Array(hmac);

  const offset = hmacBytes[hmacBytes.length - 1] & 0x0f;
  const code =
    ((hmacBytes[offset] & 0x7f) << 24) |
    ((hmacBytes[offset + 1] & 0xff) << 16) |
    ((hmacBytes[offset + 2] & 0xff) << 8) |
    (hmacBytes[offset + 3] & 0xff);

  return String(code % 10 ** digits).padStart(digits, "0");
}

function getTimeRemaining(period = 30) {
  return period - (Math.floor(Date.now() / 1000) % period);
}

// ── Main Component ─────────────────────────────────────────────
export default function Authenticator() {
  const tenant = useTenant();
  const { user } = useAuth();
  const fonts = tenant.theme?.fonts || defaultFonts;
  const [entries, setEntries] = useState([]);
  const [codes, setCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [shareEntry, setShareEntry] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const loadEntries = useCallback(async () => {
    try {
      const data = await fetchAuthenticatorEntries();
      setEntries(data.entries || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Generate codes and refresh timer
  const refreshCodes = useCallback(async () => {
    const newCodes = {};
    for (const entry of entries) {
      try {
        newCodes[entry.id] = await generateTOTP(
          entry.secret,
          entry.period,
          entry.digits,
          entry.algorithm
        );
      } catch {
        newCodes[entry.id] = "------";
      }
    }
    setCodes(newCodes);
    setTimeLeft(getTimeRemaining(entries[0]?.period || 30));
  }, [entries]);

  useEffect(() => {
    if (entries.length === 0) return;
    refreshCodes();
    intervalRef.current = setInterval(refreshCodes, 1000);
    return () => clearInterval(intervalRef.current);
  }, [entries, refreshCodes]);

  const handleDelete = async (id) => {
    if (!confirm("Remove this authenticator entry?")) return;
    try {
      await deleteAuthenticatorEntry(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopy = (id, code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleAdd = async (data) => {
    await createAuthenticatorEntry(data);
    setShowAdd(false);
    setLoading(true);
    await loadEntries();
  };

  const handleShareUpdate = async (entryId, shareWith) => {
    try {
      const updated = await shareAuthenticatorEntry(entryId, shareWith);
      setEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, sharedWith: updated.sharedWith } : e))
      );
      setShareEntry(null);
    } catch (err) {
      throw err;
    }
  };

  const ownEntries = entries.filter((e) => e.isOwner);
  const sharedEntries = entries.filter((e) => !e.isOwner);
  const period = entries[0]?.period || 30;
  const progress = timeLeft / period;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Loader2
          size={24}
          color={colors.accent}
          className="spin"
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Shield size={22} color={colors.accent} />
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              fontFamily: fonts.heading,
              color: colors.text,
              margin: 0,
            }}
          >
            Authenticator
          </h1>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background: colors.accent,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: fonts.body,
            cursor: "pointer",
          }}
        >
          <Plus size={16} />
          Add Account
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: "rgba(239,68,68,0.1)",
            color: "#ef4444",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {/* Timer bar */}
      {entries.length > 0 && (
        <div
          style={{
            height: 4,
            borderRadius: 2,
            background: colors.border || "#1e2d45",
            marginBottom: 16,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress * 100}%`,
              background:
                timeLeft <= 5
                  ? "#ef4444"
                  : timeLeft <= 10
                    ? "#f59e0b"
                    : colors.accent,
              borderRadius: 2,
              transition: "width 1s linear, background 0.3s",
            }}
          />
        </div>
      )}

      {entries.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            background: colors.bgCard || "#ffffff",
            borderRadius: 12,
            border: `1px solid ${colors.border || "#1e2d45"}`,
          }}
        >
          <KeyRound
            size={40}
            color={colors.textSecondary || "#64748b"}
            style={{ marginBottom: 12 }}
          />
          <p
            style={{
              color: colors.textSecondary || "#64748b",
              fontSize: 14,
              margin: 0,
            }}
          >
            No authenticator accounts yet. Add one to get started.
          </p>
        </div>
      ) : (
        <>
          {/* Own entries */}
          {ownEntries.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ownEntries.map((entry) => (
                <TOTPCard
                  key={entry.id}
                  entry={entry}
                  code={codes[entry.id] || "------"}
                  copied={copiedId === entry.id}
                  onCopy={() => handleCopy(entry.id, codes[entry.id])}
                  onDelete={() => handleDelete(entry.id)}
                  onShare={() => setShareEntry(entry)}
                  fonts={fonts}
                />
              ))}
            </div>
          )}

          {/* Shared with me */}
          {sharedEntries.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: ownEntries.length > 0 ? "24px 0 10px" : "0 0 10px",
                }}
              >
                <Users size={14} color={colors.textSecondary || "#64748b"} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.textSecondary || "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Shared with me
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sharedEntries.map((entry) => (
                  <TOTPCard
                    key={entry.id}
                    entry={entry}
                    code={codes[entry.id] || "------"}
                    copied={copiedId === entry.id}
                    onCopy={() => handleCopy(entry.id, codes[entry.id])}
                    fonts={fonts}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showAdd && (
        <AddEntryModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAdd}
          fonts={fonts}
        />
      )}

      {shareEntry && (
        <ShareModal
          entry={shareEntry}
          onClose={() => setShareEntry(null)}
          onSave={handleShareUpdate}
          fonts={fonts}
        />
      )}
    </div>
  );
}

// ── TOTP Card ──────────────────────────────────────────────────
function TOTPCard({ entry, code, copied, onCopy, onDelete, onShare, fonts }) {
  const [showSecret, setShowSecret] = useState(false);
  const formatted = code.length === 6
    ? `${code.slice(0, 3)} ${code.slice(3)}`
    : code;
  const isOwner = entry.isOwner;
  const sharedCount = (entry.sharedWith || []).length;

  return (
    <div
      style={{
        background: colors.bgCard || "#ffffff",
        border: `1px solid ${colors.border || "#1e2d45"}`,
        borderRadius: 10,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      {/* Issuer badge */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: isOwner ? "rgba(99,102,241,0.15)" : "rgba(34,197,94,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isOwner ? "#6366f1" : "#22c55e",
          fontWeight: 700,
          fontSize: 16,
          fontFamily: fonts.heading,
          flexShrink: 0,
        }}
      >
        {(entry.issuer || "?")[0].toUpperCase()}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: colors.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {entry.issuer}
          </span>
          {!isOwner && (
            <span
              style={{
                fontSize: 9,
                padding: "1px 5px",
                borderRadius: 4,
                background: "rgba(34,197,94,0.12)",
                color: "#22c55e",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                flexShrink: 0,
              }}
            >
              shared
            </span>
          )}
          {isOwner && sharedCount > 0 && (
            <span
              style={{
                fontSize: 9,
                padding: "1px 5px",
                borderRadius: 4,
                background: "rgba(99,102,241,0.12)",
                color: "#6366f1",
                fontWeight: 600,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Users size={9} />
              {sharedCount}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 11,
            color: colors.textSecondary || "#64748b",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {entry.accountName || (!isOwner ? `from ${entry.userEmail}` : "")}
        </div>
        {showSecret && (
          <div
            style={{
              fontSize: 10,
              color: colors.textSecondary || "#64748b",
              fontFamily: "'JetBrains Mono', monospace",
              marginTop: 4,
              wordBreak: "break-all",
            }}
          >
            {entry.secret}
          </div>
        )}
      </div>

      {/* Code */}
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          color: colors.text,
          letterSpacing: "0.08em",
          flexShrink: 0,
          cursor: "pointer",
        }}
        onClick={onCopy}
        title="Click to copy"
      >
        {formatted}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <IconBtn
          onClick={onCopy}
          title={copied ? "Copied!" : "Copy code"}
          icon={copied ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
        />
        <IconBtn
          onClick={() => setShowSecret((v) => !v)}
          title={showSecret ? "Hide secret" : "Show secret"}
          icon={showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
        />
        {isOwner && onShare && (
          <IconBtn
            onClick={onShare}
            title="Share"
            icon={<Share2 size={14} />}
          />
        )}
        {isOwner && onDelete && (
          <IconBtn
            onClick={onDelete}
            title="Delete"
            icon={<Trash2 size={14} color="#ef4444" />}
          />
        )}
      </div>
    </div>
  );
}

function IconBtn({ onClick, title, icon }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: "transparent",
        border: "none",
        padding: 6,
        borderRadius: 6,
        cursor: "pointer",
        color: colors.textSecondary || "#64748b",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </button>
  );
}

// ── Share Modal ────────────────────────────────────────────────
function ShareModal({ entry, onClose, onSave, fonts }) {
  const [emails, setEmails] = useState(entry.sharedWith || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const addEmail = () => {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErr("Invalid email address");
      return;
    }
    if (trimmed === entry.userEmail) {
      setErr("Can't share with yourself");
      return;
    }
    if (emails.includes(trimmed)) {
      setErr("Already shared with this user");
      return;
    }
    setEmails((prev) => [...prev, trimmed]);
    setInput("");
    setErr(null);
  };

  const removeEmail = (email) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(entry.id, emails);
    } catch (err) {
      setErr(err.message);
      setSaving(false);
    }
  };

  const inputStyle = {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${colors.border || "#1e2d45"}`,
    background: colors.bg || "#060b18",
    color: colors.text,
    fontSize: 13,
    fontFamily: fonts.body,
    outline: "none",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard || "#ffffff",
          border: `1px solid ${colors.border || "#1e2d45"}`,
          borderRadius: 14,
          padding: "28px 28px 24px",
          width: 440,
          maxWidth: "90vw",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Share2 size={18} color={colors.accent} />
          <h2
            style={{
              fontSize: 17,
              fontWeight: 700,
              fontFamily: fonts.heading,
              color: colors.text,
              margin: 0,
            }}
          >
            Share TOTP
          </h2>
        </div>
        <p
          style={{
            fontSize: 12,
            color: colors.textSecondary || "#64748b",
            margin: "0 0 20px",
          }}
        >
          Share <strong style={{ color: colors.text }}>{entry.issuer}</strong>{" "}
          {entry.accountName ? `(${entry.accountName})` : ""} with team members.
          They'll be able to view and copy the code.
        </p>

        {/* Email input */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter email address..."
            style={inputStyle}
            autoFocus
          />
          <button
            onClick={addEmail}
            type="button"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "0 14px",
              borderRadius: 8,
              border: "none",
              background: colors.accent,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: fonts.body,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <UserPlus size={14} />
            Add
          </button>
        </div>

        {/* Shared list */}
        {emails.length > 0 && (
          <div
            style={{
              maxHeight: 180,
              overflowY: "auto",
              marginBottom: 14,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {emails.map((email) => (
              <div
                key={email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: colors.bg || "#060b18",
                  border: `1px solid ${colors.border || "#1e2d45"}`,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: colors.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {email}
                </span>
                <button
                  onClick={() => removeEmail(email)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 4,
                    cursor: "pointer",
                    color: "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                  title="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {emails.length === 0 && (
          <div
            style={{
              padding: "20px 0",
              textAlign: "center",
              color: colors.textSecondary || "#64748b",
              fontSize: 13,
            }}
          >
            Not shared with anyone yet.
          </div>
        )}

        {err && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              background: "rgba(239,68,68,0.1)",
              color: "#ef4444",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              border: `1px solid ${colors.border || "#1e2d45"}`,
              background: "transparent",
              color: colors.textSecondary || "#64748b",
              fontSize: 13,
              fontFamily: fonts.body,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              border: "none",
              background: colors.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── QR Scanner Component ───────────────────────────────────────
function QRScanner({ onScan, onError, fonts }) {
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let scanner = null;
    let started = false;

    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!mounted) return;
        scanner = new Html5Qrcode("qr-reader");
        html5QrRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            onScan(decodedText);
            scanner.stop().catch(() => {});
            started = false;
          },
          () => {} // ignore scan failures
        );
        started = true;
        if (mounted) setScanning(true);
      } catch (err) {
        if (mounted) {
          setCamError(
            err.name === "NotAllowedError"
              ? "Camera access denied. Please allow camera access and try again."
              : "Could not start camera. Try uploading an image instead."
          );
          onError?.(err.message);
        }
      }
    })();

    return () => {
      mounted = false;
      if (scanner && started) scanner.stop().catch(() => {});
    };
  }, []);

  return (
    <div>
      <div
        id="qr-reader"
        ref={scannerRef}
        style={{
          width: "100%",
          borderRadius: 8,
          overflow: "hidden",
          marginBottom: 12,
          background: "#000",
          minHeight: scanning ? undefined : 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />
      {camError && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: "#ef4444",
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {camError}
        </div>
      )}
      {!camError && !scanning && (
        <div style={{ textAlign: "center", padding: "20px 0", color: colors.textSecondary || "#64748b", fontSize: 13 }}>
          <Loader2 size={20} className="spin" style={{ marginBottom: 6 }} />
          <div>Starting camera...</div>
        </div>
      )}
    </div>
  );
}

// ── QR File Upload Scanner ─────────────────────────────────────
function QRFileUpload({ onScan, fonts }) {
  const [err, setErr] = useState(null);
  const [pasting, setPasting] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const pasteRef = useRef(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const scanImage = useCallback(async (file) => {
    // Show preview of what we received
    const url = URL.createObjectURL(file);
    setPreview(url);

    try {
      // Use jsQR for reliable decoding via canvas pixel data
      const jsQR = (await import("jsqr")).default;
      const img = new Image();
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code) {
        setPreview(null);
        URL.revokeObjectURL(url);
        onScanRef.current(code.data);
      } else {
        setErr("No QR code found in this image. Try cropping tighter around just the QR code.");
      }
    } catch {
      setErr("Failed to process image. Try a different screenshot.");
    }
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) { setErr(null); scanImage(file); }
  };

  const handlePaste = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setPasting(true);
    setErr(null);
    setPreview(null);
    try {
      const items = await navigator.clipboard.read();
      const imageItem = items.find((item) =>
        item.types.some((t) => t.startsWith("image/"))
      );
      if (!imageItem) {
        setErr("No image found on clipboard. Copy a screenshot first (e.g. Win+Shift+S).");
        return;
      }
      const imageType = imageItem.types.find((t) => t.startsWith("image/"));
      const blob = await imageItem.getType(imageType);
      const file = new File([blob], "clipboard.png", { type: imageType });
      await scanImage(file);
    } catch (e) {
      if (e.name === "NotAllowedError") {
        setErr("Clipboard access denied. Please allow clipboard access and try again.");
      } else {
        setErr("Could not read clipboard. Try uploading the image instead.");
      }
    } finally {
      setPasting(false);
    }
  };

  // Listen for Ctrl+V / Cmd+V paste events globally
  useEffect(() => {
    const onPaste = async (e) => {
      // Check clipboardData.files first
      let file = Array.from(e.clipboardData?.files || []).find((f) =>
        f.type.startsWith("image/")
      );
      // Fallback: check clipboardData.items for image blobs
      if (!file) {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find((i) => i.type.startsWith("image/"));
        if (imageItem) file = imageItem.getAsFile();
      }
      if (file) {
        e.preventDefault();
        setErr(null);
        await scanImage(file);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [scanImage]);

  // Auto-focus the paste area so Ctrl+V works immediately
  useEffect(() => {
    pasteRef.current?.focus();
  }, []);

  return (
    <div>
      <div id="qr-file-reader" style={{ display: "none" }} />
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {/* Upload file */}
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            flex: 1,
            border: `2px dashed ${colors.border || "#1e2d45"}`,
            borderRadius: 10,
            padding: "24px 16px",
            textAlign: "center",
            cursor: "pointer",
            transition: "border-color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border || "#1e2d45")}
        >
          <Camera size={24} color={colors.textSecondary || "#64748b"} style={{ marginBottom: 6 }} />
          <div style={{ fontSize: 12, color: colors.text, fontWeight: 600, marginBottom: 2 }}>
            Upload QR code image
          </div>
          <div style={{ fontSize: 10, color: colors.textSecondary || "#64748b" }}>
            Browse for a saved screenshot
          </div>
        </div>
        {/* Paste from clipboard */}
        <div
          ref={pasteRef}
          tabIndex={0}
          onClick={handlePaste}
          onPaste={async (e) => {
            let file = Array.from(e.clipboardData?.files || []).find((f) =>
              f.type.startsWith("image/")
            );
            if (!file) {
              const items = Array.from(e.clipboardData?.items || []);
              const imageItem = items.find((i) => i.type.startsWith("image/"));
              if (imageItem) file = imageItem.getAsFile();
            }
            if (file) { e.preventDefault(); setErr(null); await scanImage(file); }
          }}
          style={{
            flex: 1,
            border: `2px dashed ${colors.border || "#1e2d45"}`,
            borderRadius: 10,
            padding: "24px 16px",
            textAlign: "center",
            cursor: pasting ? "wait" : "pointer",
            transition: "border-color 0.15s, box-shadow 0.15s",
            opacity: pasting ? 0.6 : 1,
            outline: "none",
          }}
          onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.accent}`)}
          onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border || "#1e2d45")}
        >
          <ClipboardPaste size={24} color={colors.textSecondary || "#64748b"} style={{ marginBottom: 6 }} />
          <div style={{ fontSize: 12, color: colors.text, fontWeight: 600, marginBottom: 2 }}>
            {pasting ? "Reading clipboard…" : "Paste screenshot"}
          </div>
          <div style={{ fontSize: 10, color: colors.textSecondary || "#64748b" }}>
            Ctrl+V or click to paste
          </div>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: "none" }}
      />
      {preview && (
        <div style={{ marginBottom: 12, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: colors.textSecondary || "#64748b", marginBottom: 6 }}>
            Scanned image:
          </div>
          <img
            src={preview}
            alt="Pasted screenshot"
            style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 6, border: `1px solid ${colors.border || "#1e2d45"}` }}
          />
        </div>
      )}
      {err && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: "#ef4444",
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
}

// ── Add Entry Modal ────────────────────────────────────────────
function AddEntryModal({ onClose, onAdd, fonts }) {
  const [issuer, setIssuer] = useState("");
  const [accountName, setAccountName] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("scan"); // scan, manual, or uri

  const parseOtpauthUri = (uri) => {
    try {
      const url = new URL(uri);
      if (url.protocol !== "otpauth:") throw new Error("Not an otpauth URI");
      if (url.host !== "totp") throw new Error("Only TOTP is supported");
      const label = decodeURIComponent(url.pathname.slice(1));
      const parts = label.includes(":") ? label.split(":") : [label];
      return {
        issuer: url.searchParams.get("issuer") || (parts.length > 1 ? parts[0] : ""),
        accountName: parts.length > 1 ? parts[1].trim() : parts[0],
        secret: url.searchParams.get("secret") || "",
        algorithm: url.searchParams.get("algorithm") || "SHA1",
        digits: parseInt(url.searchParams.get("digits") || "6", 10),
        period: parseInt(url.searchParams.get("period") || "30", 10),
      };
    } catch (e) {
      throw new Error("Invalid otpauth:// URI. Expected format: otpauth://totp/...");
    }
  };

  const handleQrScan = async (decodedText) => {
    setErr(null);
    setSaving(true);
    try {
      const parsed = parseOtpauthUri(decodedText);
      await onAdd(parsed);
    } catch (err) {
      setErr(err.message);
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (mode === "uri") {
        const parsed = parseOtpauthUri(secret.trim());
        await onAdd(parsed);
      } else {
        if (!issuer.trim() || !secret.trim()) {
          throw new Error("Issuer and secret are required");
        }
        await onAdd({
          issuer: issuer.trim(),
          accountName: accountName.trim(),
          secret: secret.replace(/\s/g, "").trim(),
        });
      }
    } catch (err) {
      setErr(err.message);
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${colors.border || "#1e2d45"}`,
    background: colors.bg || "#060b18",
    color: colors.text,
    fontSize: 13,
    fontFamily: fonts.body,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary || "#64748b",
    marginBottom: 4,
    display: "block",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard || "#ffffff",
          border: `1px solid ${colors.border || "#1e2d45"}`,
          borderRadius: 14,
          padding: "28px 28px 24px",
          width: 460,
          maxWidth: "90vw",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: 17,
            fontWeight: 700,
            fontFamily: fonts.heading,
            color: colors.text,
            margin: "0 0 20px",
          }}
        >
          Add Authenticator Account
        </h2>

        {/* Mode toggle */}
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 16,
            background: colors.bg || "#060b18",
            borderRadius: 8,
            padding: 3,
          }}
        >
          {[
            { id: "scan", label: "Scan QR", icon: <ScanLine size={12} /> },
            { id: "manual", label: "Manual", icon: null },
            { id: "uri", label: "Paste URI", icon: null },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              style={{
                flex: 1,
                padding: "7px 0",
                borderRadius: 6,
                border: "none",
                background: mode === m.id ? (colors.accent || "#6366f1") : "transparent",
                color: mode === m.id ? "#fff" : (colors.textSecondary || "#64748b"),
                fontSize: 12,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
              }}
            >
              {m.icon}
              {m.label}
            </button>
          ))}
        </div>

        {mode === "scan" ? (
          <>
            {/* Camera scanner on mobile, file upload everywhere */}
            <QRFileUpload onScan={handleQrScan} fonts={fonts} />

            <div style={{
              display: "flex", alignItems: "center", gap: 10, margin: "4px 0 14px",
            }}>
              <div style={{ flex: 1, height: 1, background: colors.border || "#1e2d45" }} />
              <span style={{ fontSize: 11, color: colors.textSecondary || "#64748b" }}>or use camera</span>
              <div style={{ flex: 1, height: 1, background: colors.border || "#1e2d45" }} />
            </div>

            <QRScanner
              onScan={handleQrScan}
              onError={(msg) => {}}
              fonts={fonts}
            />

            {err && (
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: "rgba(239,68,68,0.1)",
                  color: "#ef4444",
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {err}
              </div>
            )}

            {saving && (
              <div style={{ textAlign: "center", padding: "8px 0", color: colors.accent, fontSize: 13 }}>
                Adding account...
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "9px 18px",
                  borderRadius: 8,
                  border: `1px solid ${colors.border || "#1e2d45"}`,
                  background: "transparent",
                  color: colors.textSecondary || "#64748b",
                  fontSize: 13,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {mode === "uri" ? (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>otpauth:// URI</label>
                <textarea
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="otpauth://totp/Issuer:account?secret=BASE32SECRET&issuer=Issuer"
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
                  autoFocus
                />
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Issuer / Service *</label>
                  <input
                    value={issuer}
                    onChange={(e) => setIssuer(e.target.value)}
                    placeholder="e.g. GitHub, AWS, Google"
                    style={inputStyle}
                    autoFocus
                  />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Account Name</label>
                  <input
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="e.g. user@example.com"
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Secret Key (Base32) *</label>
                  <input
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder="e.g. JBSWY3DPEHPK3PXP"
                    style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
                  />
                </div>
              </>
            )}

            {err && (
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: "rgba(239,68,68,0.1)",
                  color: "#ef4444",
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {err}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "9px 18px",
                  borderRadius: 8,
                  border: `1px solid ${colors.border || "#1e2d45"}`,
                  background: "transparent",
                  color: colors.textSecondary || "#64748b",
                  fontSize: 13,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "9px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: colors.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Adding..." : "Add Account"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
